import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';

import {PulseUser, pulseUserAtom} from './auth';

// Growthbook feature flags (boolean)
export const growthbookFeatureConfig = {
  isAppBuilderPlatformEnabled: {
    label: 'App Builder Platform',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_07_13_app_builder_platform',
  },
  isAskWorkstreamUpdateEnabled: {
    label: 'Ask-powered workstream updates',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_09_09_ask_workstream_update',
  },
  isGtmPilotChangesEnabled: {
    label: 'GTM pilot changes',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_09_11_gtm_pilot_changes',
  },
  isSourcegraphEnabled: {
    label: 'Sourcegraph code search',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_09_15_sourcegraph_code_search',
  },
  isWorkstreamHeadcountInputEnabled: {
    label: 'Workstream headcount input',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_09_25_workstream_headcount_input',
  },
  isExecSponsorInboxEnabled: {
    label: 'Exec Sponsor Inbox',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_10_05_exec_sponsor_inbox',
  },
  isProductRoadmapEnabled: {
    label: 'Product Roadmap',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_10_13_product_roadmap',
  },
  isWorkstreamProductRoadmapEnabled: {
    label: 'Workstream Product Roadmap',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_10_27_workstream_product_roadmap',
  },
  isWorkstreamProductRoadmapIframeEnabled: {
    label: 'Workstream Product Roadmap Iframe',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_11_02_workstream_product_roadmap_iframe',
  },
  isAskRelaunchEnabled: {
    label: 'Search & Ask changes',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_10_30_ask_relaunch',
  },
  isHomeDirectoryEnabled: {
    label: 'Home as directory (/people → home)',
    defaultValue: false,
    growthbookFeatureId: 'company_os_2026_11_03_home_directory',
  },
} as const;

// Growthbook experiments (multi-variant string values)
export type GrowthbookExperimentEntry = {
  readonly label: string;
  readonly growthbookFeatureId: string;
  readonly defaultValue: string;
  readonly variants: readonly string[];
};

export const growthbookExperimentConfig = {} satisfies Record<string, GrowthbookExperimentEntry>;

export type GrowthbookExperimentKey = keyof typeof growthbookExperimentConfig;

export type GrowthbookExperimentValues = Record<GrowthbookExperimentKey, string>;

export const growthbookExperimentKeys = Object.keys(
  growthbookExperimentConfig
) as GrowthbookExperimentKey[];

const getExperimentEntry = (key: GrowthbookExperimentKey): GrowthbookExperimentEntry =>
  (growthbookExperimentConfig as Record<string, GrowthbookExperimentEntry>)[key];

export const defaultGrowthbookExperimentValues: GrowthbookExperimentValues = Object.fromEntries(
  growthbookExperimentKeys.map((key) => [key, getExperimentEntry(key).defaultValue])
) as GrowthbookExperimentValues;

export type GrowthbookExperimentDefinition = {
  key: GrowthbookExperimentKey;
  label: string;
  growthbookFeatureId: string;
  variants: readonly string[];
};

export const growthbookExperimentDefinitions: GrowthbookExperimentDefinition[] =
  growthbookExperimentKeys.map((key) => {
    const entry = getExperimentEntry(key);
    return {
      key,
      label: entry.label,
      growthbookFeatureId: entry.growthbookFeatureId,
      variants: entry.variants,
    };
  });

export type GrowthbookFeatureKey = keyof typeof growthbookFeatureConfig;

export type GrowthbookFeatureFlags = {
  [K in GrowthbookFeatureKey]: boolean;
};

export const growthbookFeatureKeys: GrowthbookFeatureKey[] = Object.keys(
  growthbookFeatureConfig
) as GrowthbookFeatureKey[];

export const defaultGrowthbookFeatureFlags: GrowthbookFeatureFlags = Object.fromEntries(
  growthbookFeatureKeys.map((key) => [key, growthbookFeatureConfig[key].defaultValue])
) as GrowthbookFeatureFlags;

export type GrowthbookFeatureDefinition = {
  key: GrowthbookFeatureKey;
  label: string;
  growthbookFeatureId?: string;
};

export const growthbookFeatureDefinitions: GrowthbookFeatureDefinition[] =
  growthbookFeatureKeys.map((key) => {
    const entry = growthbookFeatureConfig[key];
    return {
      key,
      label: entry.label,
      growthbookFeatureId: 'growthbookFeatureId' in entry ? entry.growthbookFeatureId : undefined,
    };
  });

export function buildGrowthbookFeatureFlagsFromSdk(
  resolveFeatureFlag: (featureId: string, defaultValue: boolean) => boolean
): GrowthbookFeatureFlags {
  const next = {...defaultGrowthbookFeatureFlags};
  for (const key of growthbookFeatureKeys) {
    const entry = growthbookFeatureConfig[key];
    if ('growthbookFeatureId' in entry && entry.growthbookFeatureId) {
      try {
        next[key] = resolveFeatureFlag(entry.growthbookFeatureId, entry.defaultValue);
      } catch {
        next[key] = entry.defaultValue;
      }
    }
  }
  return next;
}

export function buildGrowthbookExperimentValuesFromSdk(
  resolveFeatureValue: (featureId: string, defaultValue: string) => string
): GrowthbookExperimentValues {
  const next = {...defaultGrowthbookExperimentValues};
  for (const key of growthbookExperimentKeys) {
    const entry = getExperimentEntry(key);
    try {
      const value = resolveFeatureValue(entry.growthbookFeatureId, entry.defaultValue);
      (next as Record<string, string>)[key] = entry.variants.includes(value)
        ? value
        : entry.defaultValue;
    } catch {
      (next as Record<string, string>)[key] = entry.defaultValue;
    }
  }
  return next;
}

export type GrowthbookOverrideValue = 'default' | 'on' | 'off';
export type GrowthbookExperimentOverrideValue = 'default' | string;
export type GrowthbookOverrideMap = Partial<Record<GrowthbookFeatureKey, GrowthbookOverrideValue>>;
export type GrowthbookExperimentOverrideMap = Record<string, GrowthbookExperimentOverrideValue>;
export type GrowthbookOverridesByUser = Record<string, GrowthbookOverrideMap>;
export type GrowthbookExperimentOverridesByUser = Record<string, GrowthbookExperimentOverrideMap>;

export const DEFAULT_GROWTHBOOK_TEMP_OVERRIDE_TTL_MINUTES = 10;

export const growthbookFeatureIdToKey: Record<string, GrowthbookFeatureKey> =
  growthbookFeatureKeys.reduce<Record<string, GrowthbookFeatureKey>>((acc, key) => {
    const entry = growthbookFeatureConfig[key];
    if ('growthbookFeatureId' in entry && entry.growthbookFeatureId) {
      return {...acc, [entry.growthbookFeatureId]: key};
    }
    return acc;
  }, {});

export const growthbookExperimentIdToKey: Record<string, GrowthbookExperimentKey> =
  growthbookExperimentKeys.reduce<Record<string, GrowthbookExperimentKey>>((acc, key) => {
    const entry = getExperimentEntry(key);
    return {...acc, [entry.growthbookFeatureId]: key};
  }, {});

export type GrowthbookTempOverride = {
  value: Exclude<GrowthbookOverrideValue, 'default'>;
  expiresAt: number;
};
export type GrowthbookTempOverrideMap = Partial<
  Record<GrowthbookFeatureKey, GrowthbookTempOverride>
>;
export type GrowthbookTempOverridesByUser = Record<string, GrowthbookTempOverrideMap>;

export type GrowthbookTempExperimentOverride = {
  value: string;
  expiresAt: number;
};
export type GrowthbookTempExperimentOverrideMap = Record<
  string,
  GrowthbookTempExperimentOverride | undefined
>;
export type GrowthbookTempExperimentOverridesByUser = Record<
  string,
  GrowthbookTempExperimentOverrideMap
>;

export type ParsedGrowthbookOverrideParam = {
  featureKey: GrowthbookFeatureKey;
  value: GrowthbookOverrideValue;
};

export type ParsedGrowthbookExperimentOverrideParam = {
  experimentKey: GrowthbookExperimentKey;
  value: string;
};

export type ParsedGrowthbookOverrideParams = {
  overrides: ParsedGrowthbookOverrideParam[];
  experimentOverrides: ParsedGrowthbookExperimentOverrideParam[];
  ttlMinutes: number;
  matchedParamKeys: string[];
  ttlParamPresent: boolean;
};

const normalizeGrowthbookOverrideParamValue = (
  rawValue: string | null
): GrowthbookOverrideValue | null => {
  if (rawValue === null) {
    return null;
  }

  const normalized = rawValue.trim().toLowerCase();
  if (normalized === 'on' || normalized === 'true' || normalized === '1') {
    return 'on';
  }
  if (normalized === 'off' || normalized === 'false' || normalized === '0') {
    return 'off';
  }
  if (normalized === 'default' || normalized === 'clear') {
    return 'default';
  }

  return null;
};

export const parseGrowthbookOverrideParams = (
  searchParams: URLSearchParams
): ParsedGrowthbookOverrideParams => {
  const overrides: ParsedGrowthbookOverrideParam[] = [];
  const experimentOverrides: ParsedGrowthbookExperimentOverrideParam[] = [];
  const matchedParamKeys: string[] = [];

  for (const [paramKey, rawValue] of searchParams.entries()) {
    if (paramKey === 'ttl') {
      continue;
    }

    const featureKey = growthbookFeatureIdToKey[paramKey];
    if (featureKey) {
      const value = normalizeGrowthbookOverrideParamValue(rawValue);
      if (value !== null) {
        matchedParamKeys.push(paramKey);
        overrides.push({featureKey, value});
      }
      continue;
    }

    const experimentKey = growthbookExperimentIdToKey[paramKey];
    if (experimentKey) {
      const entry = getExperimentEntry(experimentKey);
      const normalized = rawValue.trim().toLowerCase();
      if (normalized === 'default' || normalized === 'clear') {
        matchedParamKeys.push(paramKey);
        experimentOverrides.push({experimentKey, value: 'default'});
      } else if (entry.variants.includes(normalized)) {
        matchedParamKeys.push(paramKey);
        experimentOverrides.push({experimentKey, value: normalized});
      }
      continue;
    }
  }

  const ttlParam = searchParams.get('ttl');
  const ttlParamPresent = ttlParam !== null;
  const parsedTtl = ttlParam === null ? NaN : Number.parseInt(ttlParam, 10);
  const ttlMinutes =
    Number.isFinite(parsedTtl) && parsedTtl > 0
      ? parsedTtl
      : DEFAULT_GROWTHBOOK_TEMP_OVERRIDE_TTL_MINUTES;

  return {
    overrides,
    experimentOverrides,
    ttlMinutes,
    matchedParamKeys,
    ttlParamPresent,
  };
};

export const getActiveTempOverridesForUser = (
  allTempOverrides: GrowthbookTempOverridesByUser,
  userKey: string,
  now = Date.now()
): GrowthbookTempOverrideMap => {
  const userOverrides = allTempOverrides[userKey] ?? {};

  return Object.fromEntries(
    Object.entries(userOverrides).filter(([, override]) => override.expiresAt > now)
  ) as GrowthbookTempOverrideMap;
};

export const getActiveTempExperimentOverridesForUser = (
  allTempOverrides: GrowthbookTempExperimentOverridesByUser,
  userKey: string,
  now = Date.now()
): GrowthbookTempExperimentOverrideMap => {
  const userOverrides = allTempOverrides[userKey] ?? {};

  return Object.fromEntries(
    Object.entries(userOverrides).filter(
      ([, override]) => override != null && override.expiresAt > now
    )
  ) as GrowthbookTempExperimentOverrideMap;
};

export const mergeGrowthbookTempFeatureFlags = (
  flags: GrowthbookFeatureFlags,
  tempOverrides: GrowthbookTempOverrideMap
): GrowthbookFeatureFlags => {
  return growthbookFeatureKeys.reduce((acc, featureKey) => {
    const tempOverride = tempOverrides[featureKey];

    return {
      ...acc,
      [featureKey]:
        tempOverride === undefined ? flags[featureKey] : tempOverride.value === 'on' ? true : false,
    };
  }, {} as GrowthbookFeatureFlags);
};

export const mergeGrowthbookTempExperimentValues = (
  values: GrowthbookExperimentValues,
  tempOverrides: GrowthbookTempExperimentOverrideMap
): GrowthbookExperimentValues => {
  return growthbookExperimentKeys.reduce((acc, key) => {
    const tempOverride = tempOverrides[key];

    return {
      ...acc,
      [key]: tempOverride === undefined ? values[key] : tempOverride.value,
    };
  }, {} as GrowthbookExperimentValues);
};

const growthbookServerFlagsStorageAtom = atomWithStorage<GrowthbookFeatureFlags>(
  'features',
  defaultGrowthbookFeatureFlags
);
const growthbookServerExperimentsStorageAtom = atomWithStorage<GrowthbookExperimentValues>(
  'experiments',
  defaultGrowthbookExperimentValues
);
const growthbookOverridesAtom = atomWithStorage<GrowthbookOverridesByUser>(
  'growthbook-overrides',
  {}
);
const growthbookExperimentOverridesAtom = atomWithStorage<GrowthbookExperimentOverridesByUser>(
  'growthbook-experiment-overrides',
  {}
);
export const growthbookTempOverridesAtom = atomWithStorage<GrowthbookTempOverridesByUser>(
  'growthbook-temp-overrides',
  {}
);
export const growthbookTempExperimentOverridesAtom =
  atomWithStorage<GrowthbookTempExperimentOverridesByUser>(
    'growthbook-temp-experiment-overrides',
    {}
  );

export const growthbookServerFlagsAtom = atom((get) => get(growthbookServerFlagsStorageAtom));
export const growthbookServerExperimentsAtom = atom((get) =>
  get(growthbookServerExperimentsStorageAtom)
);
export const growthbookReadyAtom = atom(false);

export const getGrowthbookOverrideUserKey = (user: PulseUser | null | undefined) => {
  if (user?.ldap) {
    return `ldap:${user.ldap}`;
  }
  if (user?.email) {
    return `email:${user.email}`;
  }
  return 'anonymous';
};

export const mergeGrowthbookFeatureFlags = (
  serverFlags: GrowthbookFeatureFlags,
  overrides: GrowthbookOverrideMap
): GrowthbookFeatureFlags => {
  return growthbookFeatureKeys.reduce((acc, featureKey) => {
    const overrideValue = overrides[featureKey];
    const serverValue = serverFlags[featureKey] ?? growthbookFeatureConfig[featureKey].defaultValue;

    return {
      ...acc,
      [featureKey]: overrideValue === 'on' ? true : overrideValue === 'off' ? false : serverValue,
    };
  }, {} as GrowthbookFeatureFlags);
};

export const mergeGrowthbookExperimentValues = (
  serverValues: GrowthbookExperimentValues,
  overrides: GrowthbookExperimentOverrideMap
): GrowthbookExperimentValues => {
  return growthbookExperimentKeys.reduce((acc, key) => {
    const overrideValue = overrides[key];
    const serverValue = serverValues[key] ?? getExperimentEntry(key).defaultValue;

    return {
      ...acc,
      [key]: overrideValue && overrideValue !== 'default' ? overrideValue : serverValue,
    };
  }, {} as GrowthbookExperimentValues);
};

const resolveGrowthbookServerFlagsUpdate = (
  previousFlags: GrowthbookFeatureFlags,
  nextFlags:
    | GrowthbookFeatureFlags
    | ((previousFlags: GrowthbookFeatureFlags) => GrowthbookFeatureFlags)
) => {
  return typeof nextFlags === 'function' ? nextFlags(previousFlags) : nextFlags;
};

export type GrowthbookCache = GrowthbookFeatureFlags & GrowthbookExperimentValues;

// Effective feature flag atom. Server-evaluated flags remain in `features`, and
// per-user browser-local overrides are layered on top when reading.
export const growthbookCacheAtom = atom(
  (get): GrowthbookCache => {
    const serverFlags = get(growthbookServerFlagsStorageAtom);
    const serverExperiments = get(growthbookServerExperimentsStorageAtom);
    const currentUser = get(pulseUserAtom);
    const userKey = getGrowthbookOverrideUserKey(currentUser);
    const allOverrides = get(growthbookOverridesAtom);
    const allExperimentOverrides = get(growthbookExperimentOverridesAtom);
    const allTempOverrides = get(growthbookTempOverridesAtom);
    const allTempExperimentOverrides = get(growthbookTempExperimentOverridesAtom);
    const overrides = allOverrides[userKey] ?? {};
    const experimentOverrides = allExperimentOverrides[userKey] ?? {};
    const tempOverrides = getActiveTempOverridesForUser(allTempOverrides, userKey);
    const tempExperimentOverrides = getActiveTempExperimentOverridesForUser(
      allTempExperimentOverrides,
      userKey
    );
    const mergedFlags = mergeGrowthbookFeatureFlags(serverFlags, overrides);
    const mergedExperiments = mergeGrowthbookExperimentValues(
      serverExperiments,
      experimentOverrides
    );

    return {
      ...mergeGrowthbookTempFeatureFlags(mergedFlags, tempOverrides),
      ...mergeGrowthbookTempExperimentValues(mergedExperiments, tempExperimentOverrides),
    };
  },
  (
    get,
    set,
    nextFlags: GrowthbookFeatureFlags | ((flags: GrowthbookFeatureFlags) => GrowthbookFeatureFlags)
  ) => {
    const previousFlags = get(growthbookServerFlagsStorageAtom);
    set(
      growthbookServerFlagsStorageAtom,
      resolveGrowthbookServerFlagsUpdate(previousFlags, nextFlags)
    );
  }
);

export const setGrowthbookExperimentsAtom = atom(
  null,
  (_get, set, nextValues: GrowthbookExperimentValues) => {
    set(growthbookServerExperimentsStorageAtom, nextValues);
  }
);

// Stored overrides can outlive their flag after a GrowthBook cleanup, so only
// surface keys that are still defined.
const pickKnownKeys = <T extends Record<string, unknown>>(
  overrides: T | undefined,
  knownKeys: readonly string[]
): T =>
  Object.fromEntries(
    Object.entries(overrides ?? {}).filter(([key]) => knownKeys.includes(key))
  ) as T;

export const growthbookOverridesForCurrentUserAtom = atom((get) => {
  const currentUser = get(pulseUserAtom);
  const allOverrides = get(growthbookOverridesAtom);

  return pickKnownKeys(
    allOverrides[getGrowthbookOverrideUserKey(currentUser)],
    growthbookFeatureKeys
  );
});

export const growthbookExperimentOverridesForCurrentUserAtom = atom((get) => {
  const currentUser = get(pulseUserAtom);
  const allOverrides = get(growthbookExperimentOverridesAtom);

  return pickKnownKeys(
    allOverrides[getGrowthbookOverrideUserKey(currentUser)],
    growthbookExperimentKeys
  );
});

export const setGrowthbookOverrideForCurrentUserAtom = atom(
  null,
  (
    get,
    set,
    update: {
      featureKey: GrowthbookFeatureKey;
      value: GrowthbookOverrideValue;
    }
  ) => {
    const currentUser = get(pulseUserAtom);
    const userKey = getGrowthbookOverrideUserKey(currentUser);
    const allOverrides = get(growthbookOverridesAtom);
    const nextUserOverrides = {
      ...(allOverrides[userKey] ?? {}),
    };

    if (update.value === 'default') {
      delete nextUserOverrides[update.featureKey];
    } else {
      nextUserOverrides[update.featureKey] = update.value;
    }

    const nextOverrides = {...allOverrides};

    if (Object.keys(nextUserOverrides).length === 0) {
      delete nextOverrides[userKey];
    } else {
      nextOverrides[userKey] = nextUserOverrides;
    }

    set(growthbookOverridesAtom, nextOverrides);
  }
);

export const applyGrowthbookTempOverrideAtom = atom(
  null,
  (
    get,
    set,
    update: {
      featureKey: GrowthbookFeatureKey;
      value: GrowthbookOverrideValue;
      ttlMinutes: number;
    }
  ) => {
    const currentUser = get(pulseUserAtom);
    const userKey = getGrowthbookOverrideUserKey(currentUser);
    const allTempOverrides = get(growthbookTempOverridesAtom);
    const nextUserOverrides = {
      ...(allTempOverrides[userKey] ?? {}),
    };

    if (update.value === 'default') {
      delete nextUserOverrides[update.featureKey];
    } else {
      nextUserOverrides[update.featureKey] = {
        value: update.value,
        expiresAt: Date.now() + update.ttlMinutes * 60_000,
      };
    }

    const nextTempOverrides = {...allTempOverrides};

    if (Object.keys(nextUserOverrides).length === 0) {
      delete nextTempOverrides[userKey];
    } else {
      nextTempOverrides[userKey] = nextUserOverrides;
    }

    set(growthbookTempOverridesAtom, nextTempOverrides);
  }
);

export const setGrowthbookExperimentOverrideForCurrentUserAtom = atom(
  null,
  (
    get,
    set,
    update: {
      experimentKey: GrowthbookExperimentKey;
      value: GrowthbookExperimentOverrideValue;
    }
  ) => {
    const currentUser = get(pulseUserAtom);
    const userKey = getGrowthbookOverrideUserKey(currentUser);
    const allOverrides = get(growthbookExperimentOverridesAtom);
    const nextUserOverrides = {
      ...(allOverrides[userKey] ?? {}),
    };

    if (update.value === 'default') {
      delete nextUserOverrides[update.experimentKey];
    } else {
      nextUserOverrides[update.experimentKey] = update.value;
    }

    const nextOverrides = {...allOverrides};

    if (Object.keys(nextUserOverrides).length === 0) {
      delete nextOverrides[userKey];
    } else {
      nextOverrides[userKey] = nextUserOverrides;
    }

    set(growthbookExperimentOverridesAtom, nextOverrides);
  }
);

export const applyGrowthbookTempExperimentOverrideAtom = atom(
  null,
  (
    get,
    set,
    update: {
      experimentKey: GrowthbookExperimentKey;
      value: string;
      ttlMinutes: number;
    }
  ) => {
    const currentUser = get(pulseUserAtom);
    const userKey = getGrowthbookOverrideUserKey(currentUser);
    const allTempOverrides = get(growthbookTempExperimentOverridesAtom);
    const nextUserOverrides = {
      ...(allTempOverrides[userKey] ?? {}),
    };

    if (update.value === 'default') {
      delete nextUserOverrides[update.experimentKey];
    } else {
      nextUserOverrides[update.experimentKey] = {
        value: update.value,
        expiresAt: Date.now() + update.ttlMinutes * 60_000,
      };
    }

    const nextTempOverrides = {...allTempOverrides};

    if (Object.keys(nextUserOverrides).length === 0) {
      delete nextTempOverrides[userKey];
    } else {
      nextTempOverrides[userKey] = nextUserOverrides;
    }

    set(growthbookTempExperimentOverridesAtom, nextTempOverrides);
  }
);

export const refreshGrowthbookTempOverridesAtom = atom(null, (get, set) => {
  const currentUser = get(pulseUserAtom);
  const userKey = getGrowthbookOverrideUserKey(currentUser);

  const allTempOverrides = get(growthbookTempOverridesAtom);
  const activeOverrides = getActiveTempOverridesForUser(allTempOverrides, userKey);
  const nextTempOverrides = {...allTempOverrides};
  if (Object.keys(activeOverrides).length === 0) {
    delete nextTempOverrides[userKey];
  } else {
    nextTempOverrides[userKey] = activeOverrides;
  }
  set(growthbookTempOverridesAtom, nextTempOverrides);

  const allTempExperimentOverrides = get(growthbookTempExperimentOverridesAtom);
  const activeExperimentOverrides = getActiveTempExperimentOverridesForUser(
    allTempExperimentOverrides,
    userKey
  );
  const nextTempExperimentOverrides = {...allTempExperimentOverrides};
  if (Object.keys(activeExperimentOverrides).length === 0) {
    delete nextTempExperimentOverrides[userKey];
  } else {
    nextTempExperimentOverrides[userKey] = activeExperimentOverrides;
  }
  set(growthbookTempExperimentOverridesAtom, nextTempExperimentOverrides);
});
